import React, { FunctionComponent, PropsWithChildren, useEffect, useRef } from 'react';
import { Button } from '../button/button-component';
import { Icon, IconName } from '../icons/icon/icon-component';
import styles from './page-alert-component.module.scss';

type PageAlertProps = PropsWithChildren<{
  icon: IconName;
  colour?: 'yellow' | 'teal' | 'pink' | 'green' | 'purple';
  isStackedContents?: boolean;
  isClosable?: boolean;
  showBtn?: boolean;
  btnText?: string;
  onBtnClickCallback?: () => void;
  onCloseCallback?: () => void;
}>;

/**
 * Page Alert component
 *
 * https://www.figma.com/file/Op3Jr1EuRxDkL3bHKsp5qC/Volt-(Atoms)?node-id=4933-200586&t=YMnIw28tkWUUOrSs-4
 */
export const PageAlert: FunctionComponent<PageAlertProps> = ({
  colour = 'yellow',
  isStackedContents = false,
  isClosable = false,
  ...props
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  //to set slant background colour
  useEffect(() => {
    document.documentElement.style.setProperty('--background-colour', colour ?? '');
  }, [colour]);

  return (
    <div
      className={`${styles['page-alert-component']} ${styles[`${colour}`]}  ${styles[`${isStackedContents ? 'stacked' : 'non-stacked'}`]}`}
    >
      <div className={styles['page-alert-side-wrapper']}>
        <div className={styles['page-alert-side']}>
          <div className={styles.icon}>{props.icon && <Icon name={props.icon} color='white' />}</div>
        </div>
        <div className={styles['page-alert-side-slant']} />
      </div>
      <div ref={contentRef} className={styles['page-alert-content']}>
        <div>{props.children}</div>
        {props.showBtn && (
          <Button
            buttonType='outline'
            text={props.btnText ?? 'Click me!'}
            className={styles.btn}
            data-testid='page-alert-button'
            onClick={() => {
              props.onBtnClickCallback && props.onBtnClickCallback();
            }}
          />
        )}
      </div>
      {isClosable && (
        <Icon
          name='close'
          className={styles.close}
          data-testid='page-alert-close'
          onClick={() => {
            props.onCloseCallback && props.onCloseCallback();
          }}
        />
      )}
    </div>
  );
};

export default PageAlert;
